import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const WHATSAPP_NUMBER = "916262725757";

const baseUrl = import.meta.env.VITE_URL || "";
const getImageSrc = (img) =>
  !img ? "" : img.startsWith("http") ? img : `${baseUrl}/${img.replace(/\\/g, "/")}`;

const VisaDetailsModal = ({ selectedCountry, onClose, user }) => {
  const [showMainModal, setShowMainModal] = useState(false);
  const [selectedVisaDocs, setSelectedVisaDocs] = useState(null);
  const navigate = useNavigate();

  if (!selectedCountry) return null;

  const handleSelectVisa = (visa) => {
    // Navigate to the visa application form page and pass selectedCountry and visa details in state
    navigate(`/visa/apply/${selectedCountry._id || selectedCountry.name}`, { 
      state: { selectedCountry, visaDetails: visa } 
    });
  };

  return (
    <>
      {/* ================= MAIN MODAL ================= */}
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl w-full max-w-5xl relative p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Close Button */}
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-black p-2"
              onClick={onClose}
            >
              <FaTimes size={20} />
            </button>

            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <img
                src={getImageSrc(selectedCountry.image)}
                alt={selectedCountry.name}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover shadow"
              />

              <div>
                <h2 className="text-xl sm:text-3xl font-bold">
                  {selectedCountry.name} Visa Options
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {selectedCountry.description}
                </p>
              </div>
            </div>
{/* 
            <div className="flex gap-6 mt-3 text-gray-600 text-sm">
              <p>🌎 {selectedCountry.visaCount} Visas Available</p>
            </div> */}

            <hr className="my-4" />

            {/* Visa Types */}
            <h3 className="text-lg sm:text-xl font-semibold mb-3">
              Available Visa Types
            </h3>

            {selectedCountry.visaTypes?.length > 0 ? (
              <div className="space-y-4">
                {selectedCountry.visaTypes.map((visa, index) => (
                  <div
                    key={index}
                    className="border border-blue-200 rounded-xl p-4 shadow-sm bg-blue-50/30"
                  >
                    <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                      {/* Left */}
                      <div>
                        <h4 className="text-lg font-semibold flex flex-wrap gap-2 items-center">
                          {visa.name}

                          {visa.popular && (
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                              MOST POPULAR
                            </span>
                          )}

                          <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
                            {visa.type}
                          </span>
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 mt-3 text-sm text-gray-700">
                          <p>
                            <strong>Validity:</strong> {visa.validity}
                          </p>
                          <p>
                            <strong>Duration:</strong> {visa.duration}
                          </p>
                          <p>
                            <strong>Processing:</strong> {visa.processingTime}
                          </p>
                          <p>
                            <strong>Documents:</strong>{" "}
                            {visa.requiredDocuments?.length}
                          </p>
                        </div>

                        <button
                          className="text-blue-600 text-sm underline mt-2"
                          onClick={() => {
                            setSelectedVisaDocs(visa);
                            setShowMainModal(true);
                          }}
                        >
                          View Required Documents →
                        </button>
                      </div>

                      {/* Right */}
                      <div className="lg:text-right">
                        <p className="text-gray-500 text-sm">Starting from</p>
                        <p className="text-2xl sm:text-3xl font-bold">
                          {visa.price}
                        </p>
                        <p className="text-xs text-gray-500">per person</p>

                        <button
                          onClick={() => handleSelectVisa(visa)}
                          className="mt-3 w-full lg:w-auto px-6 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                        >
                          Select Visa
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No visa types available.</p>
            )}

            {/* Important Info */}
            <div className="mt-6 p-4 rounded-xl bg-gray-50 border">
              <h4 className="flex items-center gap-2 text-lg font-semibold">
                <FaInfoCircle /> Important Information
              </h4>

              <ul className="list-disc ml-5 mt-3 text-sm text-gray-700 space-y-1">
                <li>Prices include government & service charges</li>
                <li>Processing time may vary</li>
                <li>Additional documents may be requested</li>
                <li>Refund only if visa rejected</li>
              </ul>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* ================= DOCUMENT MODAL ================= */}
      <AnimatePresence>
        {showMainModal && selectedVisaDocs && (
          <motion.div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] px-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMainModal(false)}
          >
            <motion.div
              className="bg-white w-full max-w-md p-5 rounded-xl shadow-xl"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-semibold mb-3">
                Required Documents
              </h2>

              <ul className="space-y-2 text-sm text-gray-700">
                {selectedVisaDocs.requiredDocuments?.map((doc, idx) => (
                  <li key={idx}>• {doc}</li>
                ))}
              </ul>

              <button
                onClick={() => setShowMainModal(false)}
                className="mt-4 w-full bg-gray-200 py-2 rounded-lg"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VisaDetailsModal;
